import React from 'react';
import type { ComponentConfig, Fields } from '@puckeditor/core';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';
import { isHexColor } from '../fields/hex-color';

import { BackgroundField, BACKGROUND_AI_SCHEMA, DEFAULT_BACKGROUND } from '../fields';
import type { BackgroundValue } from '../fields';
import {
  BACKGROUND_OPACITY_FIELD,
  DEFAULT_BACKGROUND_OPACITY,
  backgroundStyleWithOpacity,
  backgroundValueColor,
  resolveBackgroundOpacityFields,
} from '../fields/background-opacity';
import {
  BACKGROUND_IMAGE_FIELDS,
  DEFAULT_BACKGROUND_IMAGE_PROPS,
  resolveBackgroundImageFields,
  slotBackgroundImageStyle,
  slotBackgroundOverlayCss,
} from '../fields/background-image';
import type { BackgroundImageProps } from '../fields/background-image';
import { BORDER_COLOR_AI_SCHEMA, borderStyle, DEFAULT_BORDER, inheritedBorderColor } from '../fields/border';
import type { BorderColor, BorderValue } from '../fields/border';
import { createFlatSpacingFields, readBoxSpacing, PX_AI_SCHEMA } from '../fields/spacing-fields';
import { renderFieldGroups } from '../fields/field-groups';
import type { FieldGroupDefinition } from '../fields/field-groups';
import { BLOCK_SPACING, BLOCK_WIDTHS, CONTAINER_WIDTHS, lookup, spacingToCss } from '../fields/tokens';
import type { FlatSpacingProps } from '../fields/spacing-fields';
import type { BlockSpacing, BoxSpacing } from '../fields/tokens';
import {
  createFlatMobilePaddingFields,
  resolveFlatMobilePaddingFields,
  fillFlatMobilePadding,
} from '../fields/mobile-padding';
import type { FlatMobilePaddingProps } from '../fields/mobile-padding';
import { BorderColorField } from '../fields/widgets/border-color-field';
import { NumberField } from '../fields/widgets/number-field';
import { selectField } from '../fields/widgets/select-field';
import { toggleField } from '../fields/widgets/toggle-field';

import type { SlotProps } from './slot';
import { DEFAULT_TILE_PROPS } from './tile';
import { getTileStarterChildren } from './tile-starter';

const COLUMN_OPTIONS = [
  { label: '1 column', value: '1' },
  { label: '2 columns', value: '2' },
  { label: '3 columns', value: '3' },
  { label: '4 columns', value: '4' },
  { label: '5 columns', value: '5' },
  { label: '6 columns', value: '6' },
] as const;

const MOBILE_COLUMN_OPTIONS = [{ label: 'Inherit desktop', value: 'inherit' }, ...COLUMN_OPTIONS] as const;

const ALIGNMENT_OPTIONS = [
  { label: 'Fill', value: 'stretch' },
  { label: 'Start', value: 'start' },
  { label: 'Center', value: 'center' },
  { label: 'End', value: 'end' },
] as const;

const GAP_OPTIONS: { label: string; value: BlockSpacing }[] = [
  { label: 'None (0px)', value: '0px' as BlockSpacing },
  { label: 'Extra small (8px)', value: 'xs' },
  { label: 'Small (16px)', value: 'sm' },
  { label: 'Medium (24px)', value: 'md' },
  { label: 'Large (40px)', value: 'lg' },
  { label: 'Extra large (64px)', value: 'xl' },
  { label: '2X large (96px)', value: 'xxl' },
];

const WIDTH_OPTIONS = [
  { label: 'Fit content', value: 'fit' },
  { label: 'Fill available', value: 'fill' },
] as const;

const MAX_WIDTH_OPTIONS = [
  { label: 'Narrow', value: 'narrow' },
  { label: 'Normal', value: 'normal' },
  { label: 'Wide', value: 'wide' },
  { label: 'Full width', value: 'none' },
] as const;

const DEFAULT_COLUMNS = '3';
const DEFAULT_MOBILE_COLUMNS = '1';
const DEFAULT_GAP: BlockSpacing = 'sm';
const DEFAULT_ALIGNMENT = 'stretch';
const DEFAULT_WIDTH = 'fill';
const DEFAULT_MAX_WIDTH = 'none';
const DEFAULT_SPACING: BoxSpacing = { top: '0px', right: '0px', bottom: '0px', left: '0px' };
const DEFAULT_GRID_BACKGROUND: BackgroundValue = { ...DEFAULT_BACKGROUND, color: 'none' };
// Enabling Border should immediately show a theme hairline, without changing the shared default.
const DEFAULT_GRID_BORDER: BorderValue = { color: { style: 'theme' }, width: '1px' };
// Puck assigns fresh IDs recursively on insertion; each child starts with Tile's complete defaults.
const DEFAULT_GRID_CONTENT = Array.from({ length: 6 }, () => ({
  type: 'Tile',
  props: {
    ...DEFAULT_TILE_PROPS,
    content: (getTileStarterChildren() as unknown) as React.FC<SlotProps>,
    source: { ...DEFAULT_TILE_PROPS.source },
    alt: { ...DEFAULT_TILE_PROPS.alt },
    borderColor: { ...DEFAULT_TILE_PROPS.borderColor },
  },
}));

const ALIGNMENT_VALUES: ReadonlySet<string> = new Set(ALIGNMENT_OPTIONS.map(({ value }) => value));
const PIXEL_LENGTH = /^\d+px$/;

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function resolveColumns(value: unknown, fallback: number): number {
  let parsed = NaN;
  if (typeof value === 'number') {
    parsed = value;
  } else if (typeof value === 'string' && /^\d+$/.test(value)) {
    parsed = Number(value);
  }
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 6 ? parsed : fallback;
}

function resolveAlignment(value: unknown): string {
  return typeof value === 'string' && ALIGNMENT_VALUES.has(value) ? value : DEFAULT_ALIGNMENT;
}

function resolveGap(value: unknown): string {
  if (value === '0px') return '0px';
  return typeof value === 'string'
    ? lookup(BLOCK_SPACING, value, BLOCK_SPACING[DEFAULT_GAP])
    : BLOCK_SPACING[DEFAULT_GAP];
}

function resolveSpacing(value: unknown): BoxSpacing {
  if (!isRecord(value)) return DEFAULT_SPACING;

  return {
    top: typeof value.top === 'string' && PIXEL_LENGTH.test(value.top) ? value.top : DEFAULT_SPACING.top,
    right: typeof value.right === 'string' && PIXEL_LENGTH.test(value.right) ? value.right : DEFAULT_SPACING.right,
    bottom: typeof value.bottom === 'string' && PIXEL_LENGTH.test(value.bottom) ? value.bottom : DEFAULT_SPACING.bottom,
    left: typeof value.left === 'string' && PIXEL_LENGTH.test(value.left) ? value.left : DEFAULT_SPACING.left,
  };
}

function resolveBackground(value: unknown): BackgroundValue {
  if (!isRecord(value)) return DEFAULT_GRID_BACKGROUND;

  return { color: typeof value.color === 'string' ? value.color : DEFAULT_GRID_BACKGROUND.color };
}

function resolveBorder(value: unknown): BorderValue {
  if (!isRecord(value) || !isRecord(value.color)) return DEFAULT_BORDER;

  let { color }: { color: BorderColor } = DEFAULT_BORDER;
  if (value.color.style === 'none' || value.color.style === 'theme') {
    color = { style: value.color.style };
  } else if (value.color.style === 'custom' && typeof value.color.hex === 'string' && isHexColor(value.color.hex)) {
    color = { style: 'custom', hex: value.color.hex };
  }

  return {
    color,
    width: typeof value.width === 'string' && PIXEL_LENGTH.test(value.width) ? value.width : DEFAULT_BORDER.width,
  };
}

export type GridLayoutProps = BackgroundImageProps &
  FlatMobilePaddingProps &
  FlatSpacingProps<'margin'> &
  FlatSpacingProps<'padding'> & {
    /** Puck replaces the persisted slot array with this component before render. */
    content?: React.FC<SlotProps>;
    columns: string;
    mobileColumns: string;
    gap: BlockSpacing;
    alignItems: string;
    justifyItems: string;
    width: string;
    maxWidth: string;
    background: BackgroundValue;
    backgroundOpacity: number;
    showBorder: boolean;
    borderColor: BorderColor;
    borderWidth: string;
  };

/**
 * The slot is the grid. Keeping the children in one real Puck drop zone preserves a single ordered
 * source of truth while CSS alone changes the number of visual columns.
 */
export const StyledGridSlot = styled.div`
  display: grid;
  grid-template-columns: var(--cms-grid-template-columns, repeat(3, minmax(0, 1fr)));
  min-width: 0;

  & > * {
    min-width: 0;
  }

  &[data-puck-dropzone]:empty {
    min-width: min(100%, var(--dcm-cms-grid-empty-min-width, 128px));
  }

  @media ${mediaQueries.largePhone} {
    grid-template-columns: var(--cms-grid-mobile-template-columns, repeat(3, minmax(0, 1fr)));
  }
`;

/**
 * Grid Layout-specific styled slot element extending StyledGridSlot.
 * Owns padding via CSS custom properties so media queries can override it without !important,
 * while keeping StyledGridSlot clean so consumers do not inherit unintended padding.
 */
export const StyledGridLayoutSlot = styled(StyledGridSlot)`
  padding: var(--cms-grid-padding, 0px);
  ${slotBackgroundOverlayCss}

  @media ${mediaQueries.largePhone} {
    padding: var(--cms-grid-mobile-padding, var(--cms-grid-padding, 0px));
  }
`;

const GRID_LAYOUT_FIELD_GROUPS: readonly FieldGroupDefinition<keyof GridLayoutProps>[] = [
  {
    label: 'Appearance',
    fields: [
      'backgroundImage',
      'backgroundSize',
      'backgroundPosition',
      'backgroundRepeat',
      'overlayOpacity',
      'background',
      'backgroundOpacity',
      'showBorder',
      'borderColor',
      'borderWidth',
    ],
  },
  { label: 'Layout', fields: ['columns', 'gap', 'alignItems', 'justifyItems', 'width', 'maxWidth'] },
  { label: 'Mobile', fields: ['mobileColumns'] },
  {
    label: 'Spacing',
    fields: [
      { label: 'Padding', variant: 'card', fields: ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'] },
      'differentMobilePadding',
      {
        label: 'Mobile padding',
        variant: 'card',
        fields: ['mobilePaddingTop', 'mobilePaddingRight', 'mobilePaddingBottom', 'mobilePaddingLeft'],
      },
      { label: 'Margin', variant: 'card', fields: ['marginTop', 'marginRight', 'marginBottom', 'marginLeft'] },
    ],
  },
];

export const gridLayoutConfig: ComponentConfig<GridLayoutProps> = {
  label: 'Grid Layout',
  ai: {
    instructions:
      'A responsive CSS Grid container with equal-width columns. Add child components into its single content slot. Use child order for reading, keyboard, and visual order; do not simulate cells with repeated components or duplicate content.',
  },
  fields: {
    content: { type: 'slot' },
    ...BACKGROUND_IMAGE_FIELDS,
    background: {
      type: 'custom',
      label: 'Background color',
      render: BackgroundField,
      ai: {
        schema: BACKGROUND_AI_SCHEMA,
        instructions:
          "Background styling. Prefer a theme surface role ('none', 'surface', 'muted') to a fixed hex value.",
      },
    },
    backgroundOpacity: BACKGROUND_OPACITY_FIELD,
    showBorder: toggleField('Border'),
    borderColor: {
      type: 'custom',
      label: 'Border color',
      render: BorderColorField,
      ai: {
        schema: BORDER_COLOR_AI_SCHEMA,
        instructions: "Prefer 'theme' so the border follows merchant branding. A stored 'none' renders as 'theme'.",
      },
    },
    borderWidth: {
      type: 'custom',
      label: 'Border width',
      render: NumberField('px', DEFAULT_GRID_BORDER.width),
      ai: { schema: PX_AI_SCHEMA, instructions: 'Border width, e.g. 1px.' },
    },
    columns: selectField(
      'Desktop columns',
      COLUMN_OPTIONS,
      'Number of equal-width columns on desktop. Choose a value from 1 through 6.'
    ),
    mobileColumns: selectField(
      'Mobile columns',
      MOBILE_COLUMN_OPTIONS,
      "Use 'inherit' to keep the desktop column count on phones, or choose an explicit 1–6 column override."
    ),
    gap: {
      type: 'select',
      label: 'Gap',
      options: GAP_OPTIONS,
      ai: {
        schema: { type: 'string', enum: GAP_OPTIONS.map(({ value }) => value) },
        instructions: "Uniform row and column gap from the shared spacing scale. 'sm' is the default.",
      },
    },
    alignItems: selectField(
      'Vertical alignment',
      ALIGNMENT_OPTIONS,
      "Alignment of children within each row. 'stretch' fills the available cell height."
    ),
    justifyItems: selectField(
      'Horizontal alignment',
      ALIGNMENT_OPTIONS,
      "Alignment of children within each column. 'stretch' fills the available cell width."
    ),
    width: {
      type: 'radio',
      label: 'Width',
      options: [...WIDTH_OPTIONS],
      ai: {
        schema: { type: 'string', enum: Object.keys(BLOCK_WIDTHS) },
        instructions: "'fill' uses the available width; 'fit' shrinks to the grid's content.",
      },
    },
    maxWidth: selectField(
      'Max width',
      MAX_WIDTH_OPTIONS,
      "Caps the grid using the shared container scale. 'none' leaves it uncapped."
    ),
    ...createFlatSpacingFields('padding'),
    ...createFlatMobilePaddingFields(),
    ...createFlatSpacingFields('margin'),
  },
  renderFields: ({ fields }) => renderFieldGroups(fields, GRID_LAYOUT_FIELD_GROUPS),
  resolveData: ({ props }) => ({ props: fillFlatMobilePadding(props, DEFAULT_SPACING) }),
  resolveFields: (data, { fields }) => {
    const fieldsWithMobilePadding = resolveFlatMobilePaddingFields(
      data.props.differentMobilePadding,
      resolveBackgroundImageFields(
        data.props.backgroundImage,
        resolveBackgroundOpacityFields(backgroundValueColor(data.props.background), fields)
      )
    );
    if ((data.props.showBorder as unknown) === true) return fieldsWithMobilePadding;

    const { borderColor: _borderColor, borderWidth: _borderWidth, ...withoutBorder } = fieldsWithMobilePadding;
    return withoutBorder as Fields<GridLayoutProps>;
  },
  defaultProps: {
    content: (DEFAULT_GRID_CONTENT as unknown) as React.FC<SlotProps>,
    columns: DEFAULT_COLUMNS,
    mobileColumns: DEFAULT_MOBILE_COLUMNS,
    gap: DEFAULT_GAP,
    alignItems: DEFAULT_ALIGNMENT,
    justifyItems: DEFAULT_ALIGNMENT,
    width: DEFAULT_WIDTH,
    maxWidth: DEFAULT_MAX_WIDTH,
    paddingTop: DEFAULT_SPACING.top,
    paddingRight: DEFAULT_SPACING.right,
    paddingBottom: DEFAULT_SPACING.bottom,
    paddingLeft: DEFAULT_SPACING.left,
    differentMobilePadding: false,
    mobilePaddingTop: DEFAULT_SPACING.top,
    mobilePaddingRight: DEFAULT_SPACING.right,
    mobilePaddingBottom: DEFAULT_SPACING.bottom,
    mobilePaddingLeft: DEFAULT_SPACING.left,
    marginTop: DEFAULT_SPACING.top,
    marginRight: DEFAULT_SPACING.right,
    marginBottom: DEFAULT_SPACING.bottom,
    marginLeft: DEFAULT_SPACING.left,
    ...DEFAULT_BACKGROUND_IMAGE_PROPS,
    background: DEFAULT_GRID_BACKGROUND,
    backgroundOpacity: DEFAULT_BACKGROUND_OPACITY,
    showBorder: false,
    borderColor: DEFAULT_GRID_BORDER.color,
    borderWidth: DEFAULT_GRID_BORDER.width,
  },
  render: ({
    content: slotContent,
    columns = DEFAULT_COLUMNS,
    mobileColumns = DEFAULT_MOBILE_COLUMNS,
    gap = DEFAULT_GAP,
    alignItems = DEFAULT_ALIGNMENT,
    justifyItems = DEFAULT_ALIGNMENT,
    width = DEFAULT_WIDTH,
    maxWidth = DEFAULT_MAX_WIDTH,
    differentMobilePadding = false,
    backgroundImage = DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundImage,
    backgroundSize = DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundSize,
    backgroundPosition = DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundPosition,
    backgroundRepeat = DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundRepeat,
    overlayOpacity = DEFAULT_BACKGROUND_IMAGE_PROPS.overlayOpacity,
    background = DEFAULT_GRID_BACKGROUND,
    backgroundOpacity = DEFAULT_BACKGROUND_OPACITY,
    showBorder = false,
    borderColor = DEFAULT_GRID_BORDER.color,
    borderWidth = DEFAULT_GRID_BORDER.width,
    ...spacingProps
  }) => {
    if (!slotContent) return <></>;
    const Slot = slotContent;

    const desktopColumnCount = resolveColumns(columns, Number(DEFAULT_COLUMNS));
    const mobileColumnCount =
      mobileColumns === 'inherit' ? desktopColumnCount : resolveColumns(mobileColumns, Number(DEFAULT_MOBILE_COLUMNS));
    const gridTemplateStyle = {
      '--cms-grid-template-columns': `repeat(${desktopColumnCount}, minmax(0, 1fr))`,
      '--cms-grid-mobile-template-columns': `repeat(${mobileColumnCount}, minmax(0, 1fr))`,
    } as React.CSSProperties;
    const resolvedGap = resolveGap(gap);
    const resolvedWidth =
      typeof width === 'string'
        ? lookup(BLOCK_WIDTHS, width, BLOCK_WIDTHS[DEFAULT_WIDTH])
        : BLOCK_WIDTHS[DEFAULT_WIDTH];
    const resolvedMaxWidth =
      typeof maxWidth === 'string'
        ? lookup(CONTAINER_WIDTHS, maxWidth, CONTAINER_WIDTHS[DEFAULT_MAX_WIDTH])
        : CONTAINER_WIDTHS[DEFAULT_MAX_WIDTH];

    const resolvedPadding = resolveSpacing(readBoxSpacing(spacingProps, 'padding'));
    const resolvedMobilePadding =
      (differentMobilePadding as unknown) === true
        ? resolveSpacing(readBoxSpacing(spacingProps, 'mobilePadding'))
        : resolvedPadding;

    const slotStyle = {
      ...backgroundStyleWithOpacity(resolveBackground(background), backgroundOpacity),
      ...slotBackgroundImageStyle({
        backgroundImage,
        backgroundSize,
        backgroundPosition,
        backgroundRepeat,
        overlayOpacity,
      }),
      ...((showBorder as unknown) === true
        ? borderStyle(resolveBorder({ color: inheritedBorderColor(borderColor), width: borderWidth }))
        : {}),
      ...gridTemplateStyle,
      '--cms-grid-padding': spacingToCss(resolvedPadding),
      '--cms-grid-mobile-padding': spacingToCss(resolvedMobilePadding),
      gap: resolvedGap,
      alignItems: resolveAlignment(alignItems),
      justifyItems: resolveAlignment(justifyItems),
      width: resolvedWidth,
      maxWidth: resolvedMaxWidth,
      margin: '0 auto',
      boxSizing: 'border-box',
    } as React.CSSProperties;

    return (
      <div style={{ margin: spacingToCss(resolveSpacing(readBoxSpacing(spacingProps, 'margin'))), minWidth: 0 }}>
        <Slot as={StyledGridLayoutSlot} minEmptyHeight={128} style={slotStyle} />
      </div>
    );
  },
};
